<template>
  <div class="setup-hub-add-users-content">
    <div class="subtitle1 text-primary mb-1">
      {{ $t("setupHub.subSteps.addProtectedUsers.descriptionTitle") }}
    </div>
    <div class="body2 text-indigo-medium mb-6">
      {{ $t("setupHub.subSteps.addProtectedUsers.description") }}
    </div>
    <div class="d-flex flex-column gap-4 mb-4">
      <div class="mb-2" v-for="item in actions" :key="item.action">
        <setup-hub-option-card @click="openModal(item.action)">
          <div>
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon :icon="item.icon" size="24" color="primary" />
                <div class="ml-1 subtitle2 text-primary">
                  {{ $t(`setupHub.subSteps.addProtectedUsers.options.${item.action}.title`) }}
                </div>
              </div>

              <v-chip
                v-if="item.recommended"
                variant="flat"
                size="x-small"
                color="indigo-sky"
                class="recommended-chip"
                label
                rounded="xs"
              >
                <v-icon icon="$star" size="14" />
                <span class="ml-1 text-primary text-uppercase coro-label-sm">
                  {{ $t("setupHub.recommended") }}
                </span>
              </v-chip>
            </div>

            <div class="body3 text-indigo-medium">
              {{ $t(`setupHub.subSteps.addProtectedUsers.options.${item.action}.description`) }}
            </div>
          </div>
        </setup-hub-option-card>
      </div>
    </div>

    <div class="subtitle2 text-primary mb-2">
      {{ $t("setupHub.subSteps.addProtectedUsers.csvTitle") }}
    </div>
    <div class="coro-link body2" @click="openModal(UserAction.IMPORT_USERS_FROM_CSV)">
      {{ $t("setupHub.subSteps.addProtectedUsers.csvLink") }} ›
    </div>

    <v-divider class="mt-5 mb-5" />

    <div class="d-flex align-center" @click="openHelpArticle()">
      <div class="mr-1 body2 coro-link">{{ $t("setupHub.readHelpArticle") }}</div>
      <v-icon icon="$externalLink" size="14" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, type PropType } from "vue";
import SetupHubOptionCard from "@/components/setup-hub/shared/SetupHubOptionCard.vue";
import { UserAction, UserGroupAction } from "@/constants/users.ts";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module.ts";
import { storeToRefs } from "pinia";
import { useUserActions } from "@/composables/useUserActions.ts";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";
import { SetupHubStep, setupHubSubStepLinks } from "@/constants/setup-hub.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    subStep: {
      type: String as PropType<SetupHubStep>,
      required: true,
    },
  },
  components: {
    SetupHubOptionCard,
  },
  setup(props) {
    const sidebarPanelStore = useSidebarPanelStore();
    const cloudAppsStore = useCloudAppsStore();
    const { openModal: openUserModal } = useUserActions();

    const { connectedServices } = storeToRefs(cloudAppsStore);

    const openModal = (action: string) => {
      trackGtmEvent(
        GtmAction.SETUP_HUB,
        `${startCase(props.subStep)} - ${startCase(action)} - ${GTMParams.ACTION_CLICKED}`
      );
      sidebarPanelStore.closeSidebar();
      openUserModal({
        action,
        connectedServices: connectedServices.value,
      });
    };

    const actions = [
      {
        action: UserAction.ADD_ALL_USERS,
        recommended: true,
        icon: "icon-protection-xs",
      },
      {
        action: UserGroupAction.ADD_NEW_GROUP,
        recommended: false,
        icon: "icon-group-xs",
      },
      {
        action: UserAction.ADD_USERS,
        recommended: false,
        icon: "$userXs",
      },
    ];

    const openHelpArticle = () => {
      trackGtmEvent(
        GtmAction.SETUP_HUB,
        `${startCase(props.subStep)} - ${GTMParams.HELP_LINK_CLICKED}`
      );
      window.open(setupHubSubStepLinks[props.subStep], "_blank");
    };

    onMounted(async () => {
      await cloudAppsStore.getServices();
    });

    return {
      actions,
      openModal,
      openHelpArticle,
      UserAction,
    };
  },
});
</script>

<style scoped lang="scss">
.setup-hub-add-users-content {
  display: flex;
  flex-direction: column;
}

.recommended-chip {
  margin-top: -15px;
  margin-right: -5px;
}
</style>
